import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Locale } from '@/lib/i18n/config'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Format a timestamp for display in the given IANA timezone */
export function formatDateTime(date: Date | string, timezone: string): string {
  return new Date(date).toLocaleString('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/** Format just the date portion in the given timezone (YYYY-MM-DD) */
export function formatDate(date: Date | string, timezone: string): string {
  return new Date(date).toLocaleDateString('en-CA', { timeZone: timezone })
}

/** Format just the time portion in the given timezone (HH:MM) */
export function formatTime(date: Date | string, timezone: string): string {
  return new Date(date).toLocaleTimeString('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/**
 * Get the IANA timezone offset string (e.g. "+08:00", "-05:00") for a given timezone at a given moment.
 * Uses Intl to extract the actual offset including DST.
 */
export function getTimezoneOffset(timezone: string, at: Date = new Date()): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    timeZoneName: 'longOffset',
  })
  // Returns something like "4/7/2026, GMT+08:00" or "4/7/2026, GMT-05:00"
  const parts = formatter.formatToParts(at)
  const tzPart = parts.find(p => p.type === 'timeZoneName')
  const offset = tzPart?.value?.replace('GMT', '') || '+00:00'
  // Handle "GMT" (UTC) case where offset is empty
  return offset === '' ? '+00:00' : offset
}

/**
 * Convert a wall-clock string (`<input type="datetime-local">` value
 * like `"2024-05-15T14:30"`) into a UTC ISO string by interpreting it
 * AS-IF the wall clock were in `timezone`, NOT the browser's tz.
 *
 * Why: `new Date("2024-05-15T14:30").toISOString()` reads the input as
 * BROWSER local time. A HK merchant on a Sydney VPN device typing
 * "May 15 14:30" would otherwise get UTC corresponding to 14:30 Sydney
 * (04:30 UTC) instead of 14:30 HK (06:30 UTC) — coupon windows / event
 * schedules silently shift hours.
 *
 * Returns null when the input is empty so callers can pass it straight
 * through to a nullable column.
 */
export function wallClockInTzToISO(localInput: string, timezone: string): string | null {
  if (!localInput) return null
  // `localInput` like "2024-05-15T14:30" with no tz info. Treat it as
  // a wall-clock instant in `timezone`. Strategy: build the ISO by
  // appending the tz's offset at that moment, then parse.
  //
  // DST edge: getTimezoneOffset returns the offset for the merchant tz
  // at the wall-clock moment as if it were UTC — a small drift exists
  // around the DST transition (1 hour at most). For coupon windows /
  // human-set deadlines this is a non-issue; for sub-hour-precision
  // workflows a more careful approach would be needed.
  const probe = new Date(localInput + ':00Z')
  const offset = getTimezoneOffset(timezone, probe) // e.g. "+08:00"
  return new Date(`${localInput}:00${offset}`).toISOString()
}

/**
 * Get today's date string (YYYY-MM-DD) in the given timezone.
 */
export function getToday(timezone: string): string {
  return new Date().toLocaleDateString('en-CA', { timeZone: timezone })
}

/**
 * Get a date range for a period (today, tomorrow, week, next_week, month)
 * in the given timezone. Returns ISO strings with correct timezone offset.
 */
export function getDateRange(
  period: 'today' | 'tomorrow' | 'week' | 'next_week' | 'month',
  timezone: string
): { from: string; to: string } {
  const now = new Date()
  const offset = getTimezoneOffset(timezone, now)
  // Parse local date components in the target timezone
  const localDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
  const y = localDate.getFullYear()
  const m = localDate.getMonth()
  const d = localDate.getDate()
  const dow = localDate.getDay() // 0=Sun

  const makeISO = (date: Date) => {
    const yy = date.getFullYear()
    const mm = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    return `${yy}-${mm}-${dd}`
  }

  const dayRange = (start: Date, end: Date) => ({
    from: `${makeISO(start)}T00:00:00${offset}`,
    to: `${makeISO(end)}T23:59:59${offset}`,
  })

  switch (period) {
    case 'today':
      return dayRange(new Date(y, m, d), new Date(y, m, d))
    case 'tomorrow':
      return dayRange(new Date(y, m, d + 1), new Date(y, m, d + 1))
    case 'week':
      return dayRange(new Date(y, m, d - dow + 1), new Date(y, m, d - dow + 7))
    case 'next_week':
      return dayRange(new Date(y, m, d - dow + 8), new Date(y, m, d - dow + 14))
    case 'month':
      return dayRange(new Date(y, m, 1), new Date(y, m + 1, 0))
  }
}

/**
 * Build an ISO timestamp string for a date + time in the given timezone.
 * e.g. toTimezoneISO('2026-04-07', '14:30', 'America/New_York') → '2026-04-07T14:30:00-04:00'
 */
export function toTimezoneISO(date: string, time: string, timezone: string): string {
  // Compute offset for the target date
  const refDate = new Date(`${date}T12:00:00Z`) // noon UTC to avoid edge issues
  const offset = getTimezoneOffset(timezone, refDate)
  return `${date}T${time}:00${offset}`
}

/**
 * Build day boundary ISO strings for a given date in the given timezone.
 * Returns { start: '2026-04-07T00:00:00+08:00', end: '2026-04-07T23:59:59+08:00' }
 */
export function getDayBounds(date: string, timezone: string): { start: string; end: string } {
  const refDate = new Date(`${date}T12:00:00Z`)
  const offset = getTimezoneOffset(timezone, refDate)
  return {
    start: `${date}T00:00:00${offset}`,
    end: `${date}T23:59:59${offset}`,
  }
}

/**
 * Get the current local date object components in a timezone (for advanced date math).
 */
export function getLocalNow(timezone: string): { year: number; month: number; date: number; day: number; hours: number; minutes: number } {
  const now = new Date()
  const local = new Date(now.toLocaleString('en-US', { timeZone: timezone }))
  return {
    year: local.getFullYear(),
    month: local.getMonth(),
    date: local.getDate(),
    day: local.getDay(),
    hours: local.getHours(),
    minutes: local.getMinutes(),
  }
}

/**
 * Relative time display, locale-aware.
 *
 * `locale` defaults to `'en'` — passing it from a React component is the
 * normal path (`const { locale } = useLocale(); timeAgo(date, locale)`),
 * but server-side callers without a request context still get a sensible
 * English fallback instead of silently rendering Traditional Chinese.
 *
 * Examples per locale:
 *   en       → "just now" / "5m ago" / "2h ago" / "3d ago" / "4mo ago" / "2y ago"
 *   zh-Hant  → "剛剛" / "5 分鐘前" / "2 小時前" / "3 天前" / "4 個月前" / "2 年前"
 *   zh-Hans  → "刚刚" / "5 分钟前" / "2 小时前" / "3 天前" / "4 个月前" / "2 年前"
 */
export function timeAgo(date: Date | string, locale: Locale = 'en'): string {
  const now = Date.now()
  const then = new Date(date).getTime()
  const diffMs = now - then

  const minutes = Math.floor(Math.max(diffMs, 0) / 60000)
  if (diffMs < 0 || minutes < 1) return justNow(locale)

  if (minutes < 60) return formatMinutes(minutes, locale)

  const hours = Math.floor(minutes / 60)
  if (hours < 24) return formatHours(hours, locale)

  const days = Math.floor(hours / 24)
  if (days < 30) return formatDays(days, locale)

  const months = Math.floor(days / 30)
  if (months < 12) return formatMonths(months, locale)

  return formatYears(Math.floor(months / 12), locale)
}

function justNow(locale: Locale): string {
  switch (locale) {
    case 'zh-Hant': return '剛剛'
    case 'zh-Hans': return '刚刚'
    case 'en':      return 'just now'
  }
}

function formatMinutes(n: number, locale: Locale): string {
  switch (locale) {
    case 'zh-Hant': return `${n}分鐘前`
    case 'zh-Hans': return `${n}分钟前`
    case 'en':      return `${n}m ago`
  }
}

function formatHours(n: number, locale: Locale): string {
  switch (locale) {
    case 'zh-Hant': return `${n}小時前`
    case 'zh-Hans': return `${n}小时前`
    case 'en':      return `${n}h ago`
  }
}

function formatDays(n: number, locale: Locale): string {
  switch (locale) {
    case 'zh-Hant': return `${n}日前`
    case 'zh-Hans': return `${n}天前`
    case 'en':      return `${n}d ago`
  }
}

function formatMonths(n: number, locale: Locale): string {
  switch (locale) {
    case 'zh-Hant': return `${n}個月前`
    case 'zh-Hans': return `${n}个月前`
    case 'en':      return `${n}mo ago`
  }
}

function formatYears(n: number, locale: Locale): string {
  switch (locale) {
    case 'zh-Hant': return `${n}年前`
    case 'zh-Hans': return `${n}年前`
    case 'en':      return `${n}y ago`
  }
}

/** Check if two timestamps are within N hours of each other */
export function withinHours(a: Date | string, b: Date | string, hours: number): boolean {
  const diff = Math.abs(new Date(a).getTime() - new Date(b).getTime())
  return diff <= hours * 60 * 60 * 1000
}

/** Zero-decimal currencies where Stripe expects whole units (no cents) */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'JPY', 'KMF', 'KRW', 'MGA',
  'PYG', 'RWF', 'UGX', 'VND', 'VUV', 'XAF', 'XOF', 'XPF',
])

/** Format currency amount with symbol. Defaults to HKD. */
export function formatCurrency(amount: number, currency = 'HKD'): string {
  const code = currency.toUpperCase()
  try {
    return new Intl.NumberFormat('en', {
      style: 'currency',
      currency: code,
      minimumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 0,
      maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2,
    }).format(amount)
  } catch {
    return `${code} ${amount.toLocaleString()}`
  }
}

/** @deprecated Use formatCurrency(amount, 'HKD') instead */
export function formatHKD(amount: number): string {
  return formatCurrency(amount, 'HKD')
}

/** Returns true when the input is a synthetic channel id (sandbox personas,
 * Instagram opaque ids, Telegram numeric chat_ids that aren't phones, etc.)
 * rather than a dialable phone number. The customers table reuses
 * `channel_user_id` as the phone fallback because for WhatsApp it IS the
 * phone — but for sandbox personas (`sandbox-new`, `sandbox-vip`) and
 * non-phone channels the value would render as `+sandbox-new`. */
export function isSyntheticPhone(raw: string | null | undefined): boolean {
  if (!raw) return true
  const cleaned = raw.replace(/^\+/, '').trim()
  if (!cleaned) return true
  if (cleaned.startsWith('sandbox-')) return true
  if (cleaned.startsWith('qa-')) return true
  // A real phone is mostly digits — anything with letters or fewer than 6
  // digits isn't a dialable number.
  const digits = cleaned.replace(/[^\d]/g, '')
  if (digits.length < 6) return true
  if (/[a-z]/i.test(cleaned)) return true
  return false
}

/** Format phone number with country code: "85296111001" → "+852 9611 1001".
 * Returns the raw value unchanged for synthetic ids — callers should check
 * `isSyntheticPhone()` first when they want to show an empty state instead.
 *
 * 8-digit values starting 2/3/5/6/7/8/9 are assumed HK local numbers and
 * the +852 prefix is added before grouping — HK SMEs commonly enter
 * "91234567" without country code, and the un-prefixed fallback `+91234567`
 * looks broken. */
export function formatPhone(raw: string): string {
  if (isSyntheticPhone(raw)) return raw
  // Strip leading + if present (DB may store with or without)
  raw = raw.replace(/^\+/, '')
  if (/^[23567-9]\d{7}$/.test(raw)) raw = '852' + raw
  // Common country code lengths (longest match first)
  const codes: [string, number[]][] = [
    ['852',  [4, 4]],       // HK: 9611 1001
    ['853',  [4, 4]],       // Macau
    ['886',  [3, 3, 3]],    // Taiwan: 912 345 678
    ['86',   [3, 4, 4]],    // China: 138 1234 5678
    ['65',   [4, 4]],       // Singapore
    ['60',   [3, 4, 4]],    // Malaysia
    ['1',    [3, 3, 4]],    // US/Canada: 234 567 8901
    ['44',   [4, 6]],       // UK
    ['81',   [2, 4, 4]],    // Japan
    ['61',   [3, 3, 3]],    // Australia
  ]

  for (const [code, groups] of codes) {
    if (raw.startsWith(code)) {
      const local = raw.slice(code.length)
      const parts: string[] = []
      let pos = 0
      for (const len of groups) {
        parts.push(local.slice(pos, pos + len))
        pos += len
      }
      if (pos < local.length) parts[parts.length - 1] += local.slice(pos)
      return `+${code} ${parts.join(' ')}`
    }
  }

  // Fallback: just add +
  return `+${raw}`
}

/** Brand-cased display name for a message channel: "whatsapp" → "WhatsApp".
 * Never render a raw channel enum (or .toUpperCase() of one) in the UI —
 * "WHATSAPP" reads as debug output. Unknown channels fall back to a
 * capitalised form so new channels degrade gracefully. */
export function channelDisplayName(channel: string): string {
  const names: Record<string, string> = {
    whatsapp: 'WhatsApp',
    line: 'LINE',
    instagram: 'Instagram',
    telegram: 'Telegram',
    web: 'Web',
    email: 'Email',
    sms: 'SMS',
  }
  return names[channel] ?? (channel ? channel.charAt(0).toUpperCase() + channel.slice(1) : channel)
}

/** Convert amount to Stripe smallest unit (e.g. cents). Zero-decimal currencies stay as-is. */
export function toStripeAmount(amount: number, currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase())
    ? Math.round(amount)
    : Math.round(amount * 100)
}

/** Inverse of toStripeAmount: Stripe's smallest unit back to a major-unit amount. */
export function fromStripeAmount(amount: number, currency: string): number {
  return ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? amount : amount / 100
}
